import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import FastForwardIcon from "@mui/icons-material/FastForward";
import Replay from "@mui/icons-material/Replay";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbsUpDownIcon from "@mui/icons-material/ThumbsUpDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import WorkIcon from "@mui/icons-material/Work";
import WorkOffIcon from "@mui/icons-material/WorkOff";
import WrongLocationIcon from "@mui/icons-material/WrongLocation";
import { Fab, Grid, Tooltip, useTheme } from "@mui/material";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import React from "react";
import type { Actions } from "./types";

export type Action = {
	icon: React.ReactNode;
	name: string;
	description: string;
	action: Actions;
	/** react-hotkeys-hook key string — bound as-is by the grading view. */
	hotkey: string;
};

/** Stable data-component suffix, shared by the buttons and the shortcut panel. */
export const actionSlug = (action: Action): string =>
	action.action.replace(/\s+/g, "-");

/** Panel label for a hotkey: the raw key, spelled out where it reads badly. */
export const formatHotkey = (hotkey: string): string => {
	if (hotkey === "right") return "right-arrow";
	if (hotkey === "left") return "left-arrow";
	if (hotkey === "mod+z") return "ctrl+z";
	return hotkey;
};

export const mainActions: Action[] = [
	{
		icon: <ThumbUpIcon />,
		name: "Up",
		description: "Good match! We'll start contacting this person for this role.",
		action: "up",
		hotkey: "right",
	},
	{
		icon: <ThumbDownIcon />,
		name: "Down",
		description: "Not a good match. Helps improve future suggestions.",
		action: "down",
		hotkey: "left",
	},
	{
		icon: <Replay />,
		name: "Undo",
		description: "Go back to previous candidate.",
		action: "undo",
		hotkey: "mod+z",
	},
];

export const actions: Action[] = [
	{
		icon: <WrongLocationIcon />,
		name: "Candidate in wrong location",
		description: "Wrong location for this role. Won't appear in this queue again.",
		action: "wrong location",
		hotkey: "w",
	},
	{
		icon: <WorkOffIcon />,
		name: "Not freelancer",
		description: "Not a freelancer. Won't appear in any freelancer queues.",
		action: "inte egenkonsult",
		hotkey: "i",
	},
	{
		icon: <WorkIcon />,
		name: "Freelancer",
		description: "Is freelancer but wrong fit. Won't appear in this queue again.",
		action: "egenkonsult",
		hotkey: "e",
	},
	{
		icon: <SkipNextIcon />,
		name: "Skip",
		description:
			"Can't vote (e.g. personal connection). Won't appear in this queue again.",
		action: "skip",
		hotkey: "s",
	},
	{
		icon: <AccessTimeIcon />,
		name: "Later",
		description: "Review later. Will reappear in about 2 weeks.",
		action: "later",
		hotkey: "l",
	},
	{
		icon: <FastForwardIcon />,
		name: "Next",
		description: "Skip without voting. Will reappear in about 2 hours.",
		action: "next",
		hotkey: "n",
	},
	{
		icon: <SwapHorizIcon />,
		name: "Vote other role",
		description: "Upvote this person for a different assignment.",
		action: "vote other",
		hotkey: "v",
	},
];

// Actions only for egenkonsult assignments
const EGENKONSULT_ONLY: Actions[] = ["inte egenkonsult", "egenkonsult"];
// Actions hidden for egenkonsult assignments (only down, up is still shown)
const HIDDEN_FOR_EGENKONSULT: Actions[] = [];

/**
 * Get filtered actions based on assignment type
 * - egenkonsult=true: up, wrong location, inte egenkonsult, egenkonsult, skip, later (NO down)
 * - egenkonsult=false: up, down, wrong location, skip, later (NO inte egenkonsult/egenkonsult)
 */
export const getActionsForAssignment = (isEgenkonsult: boolean): Action[] => {
	const allActions = [...mainActions, ...actions].filter((a) => a.action !== "undo");
	if (isEgenkonsult) {
		// Hide only down for egenkonsult assignments (up is still shown)
		return allActions.filter((a) => !HIDDEN_FOR_EGENKONSULT.includes(a.action));
	}
	// Hide inte egenkonsult/egenkonsult for regular assignments
	return allActions.filter((a) => !EGENKONSULT_ONLY.includes(a.action));
};

/**
 * Get shortcut actions for help menu (includes undo)
 */
export const queueToggle: Action = {
	icon: <ViewSidebarIcon />,
	name: "Queue",
	description: "Toggle the queue sidebar.",
	action: "queue",
	hotkey: "q",
};

/** Up + draft a single pitch — not a grade, so it stays out of the SpeedDial. */
export const draftPitch: Action = {
	icon: <ChatBubbleOutlineIcon />,
	name: "Draft pitch",
	description: "Up + draft a pitch (AI / Template / Manual)",
	action: "draft pitch",
	hotkey: "p",
};

export const getShortcutsForAssignment = (isEgenkonsult: boolean): Action[] => {
	const undo = mainActions.find((a) => a.action === "undo");
	const filtered = getActionsForAssignment(isEgenkonsult);
	return undo
		? [undo, ...filtered, draftPitch, queueToggle]
		: [...filtered, draftPitch, queueToggle];
};

const ActionButtons = ({
	disableUndo,
	handleUndo,
	handleCreateVote,
	handleNext,
	handleVoteOther,
	handleDraftPitch,
	info,
	isEgenkonsult = false,
	canHide = false,
	isHidden = false,
	onToggleHidden,
}: {
	disableUndo: boolean;
	handleUndo: () => void;
	handleCreateVote: (grade: string) => void;
	handleNext: () => void;
	handleVoteOther: () => void;
	handleDraftPitch: () => void;
	info: React.ReactNode;
	isEgenkonsult?: boolean;
	canHide?: boolean;
	isHidden?: boolean;
	onToggleHidden?: (hidden: boolean) => void;
}) => {
	const theme = useTheme();

	// Filter speedDial actions based on assignment type
	// egenkonsult: hide nothing from speed dial (all secondary actions are valid)
	// non-egenkonsult: hide inte egenkonsult/egenkonsult from speed dial
	const speedDialActions = isEgenkonsult
		? actions
		: actions.filter((a) => !EGENKONSULT_ONLY.includes(a.action));

	if (canHide && isHidden) {
		return (
			<Tooltip title="Visa röstknappar">
				<Fab
					data-component="show-action-buttons-fab"
					size="small"
					onClick={() => onToggleHidden?.(false)}
					sx={{
						position: "fixed",
						bottom: (theme) => theme.spacing(2),
						right: (theme) => theme.spacing(2),
						zIndex: 1100,
						backgroundColor: (theme) => theme.palette.background.paper,
					}}
				>
					<ThumbsUpDownIcon />
				</Fab>
			</Tooltip>
		);
	}

	return (
		<Grid
			data-component="action-buttons-container"
			container
			sx={{
				position: "fixed",
				bottom: (theme) => theme.spacing(2),
				left: 0,
				right: 0,
				width: "fit-content",
				mx: "auto",
				display: "flex",
				justifyContent: "center",
				alignItems: "end",
				zIndex: 1100, //Drawer has a x-index of 1200 - wanted to place these jsut under :)
			}}
			spacing={2}
			className="ActionButtonsContainer"
		>
			<Grid data-component="down-button-cell" padding="0">
				<Fab
					data-component="down-fab"
					onClick={() => handleCreateVote("down")}
					sx={{
						"&.MuiFab-root": {
							"&:hover": {
								backgroundColor: theme.actionButtons.thumbDown.dark,
							},
						},
						backgroundColor: (theme) => theme.actionButtons.thumbDown.main,
						color: "common.white",
					}}
				>
					<ThumbDownIcon />
				</Fab>
			</Grid>
			<Grid data-component="undo-button-cell">
				<Fab
					data-component="undo-fab"
					onClick={handleUndo}
					disabled={disableUndo}
					sx={{
						backgroundColor: (theme) => theme.actionButtons.undo.main,
						"&:hover": {
							backgroundColor: (theme) => theme.actionButtons.undo.dark,
						},
						"&.Mui-disabled": {
							opacity: 0.75,
							backgroundColor: (theme) => theme.actionButtons.undo.main,
						},
					}}
				>
					<Replay />
				</Fab>
			</Grid>
			{/* Up button - always shown */}
			<Grid data-component="up-button-cell">
				<Fab
					data-component="up-fab"
					onClick={() => handleCreateVote("up")}
					sx={{
						"&.MuiFab-root": {
							"&:hover": {
								backgroundColor: theme.palette.success.main,
							},
						},
						backgroundColor: (theme) => theme.palette.success.light,
						color: "common.white",
					}}
				>
					<ThumbUpIcon />
				</Fab>
			</Grid>
			{/* Up + draft a single pitch (warm follow-up or cold opener, chosen
			    automatically server-side from the prior-conversation signal) and
			    review it in the unified 3-tab dialog (AI / Template / Manual). */}
			<Grid data-component="draft-pitch-button-cell">
				<Tooltip title={draftPitch.description}>
					<Fab
						data-component="draft-pitch-fab"
						onClick={handleDraftPitch}
						sx={{
							backgroundColor: (theme) => theme.palette.success.light,
							color: "common.white",
							"&:hover": {
								backgroundColor: (theme) => theme.palette.success.main,
							},
						}}
					>
						{draftPitch.icon}
					</Fab>
				</Tooltip>
			</Grid>
			<Grid
				data-component="speed-dial-cell"
				sx={{ position: "relative", width: 56, height: 56 }}
			>
				<SpeedDial
					data-component="actions-speed-dial"
					ariaLabel="SpeedDial basic example"
					icon={<SpeedDialIcon />}
					sx={{ position: "absolute", bottom: 0, right: 0 }}
				>
					{speedDialActions.map((action) => (
						<SpeedDialAction
							data-component={`speed-dial-action-${actionSlug(action)}`}
							key={action.name}
							icon={action.icon}
							slotProps={{ tooltip: { title: action.name } }}
							onClick={() => {
								if (action.action === "next") {
									handleNext();
								} else if (action.action === "vote other") {
									handleVoteOther();
								} else {
									handleCreateVote(action.action);
								}
							}}
						/>
					))}
				</SpeedDial>
			</Grid>
			<Grid data-component="info-cell">{info}</Grid>
			{canHide && (
				<Grid data-component="hide-button-cell">
					<Tooltip title="Dölj röstknappar">
						<Fab
							data-component="hide-action-buttons-fab"
							size="small"
							onClick={() => onToggleHidden?.(true)}
							sx={{
								backgroundColor: (theme) =>
									theme.palette.background.paper,
							}}
						>
							<CloseIcon />
						</Fab>
					</Tooltip>
				</Grid>
			)}
		</Grid>
	);
};

export default ActionButtons;
